import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@gymlog/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Not a hypothesis: measured in
 * `space-academy-app` (same canon, local stack `activity` table, course
 * `06a131d0`) — course is created, 1.03 s later unit is created and **in
 * between there is NO `course/updated` op**: the title the author had just
 * written never generated a CRUD operation. In a run where
 * everything queued before the first upload (`136ef638`) it is present, 7 ms from
 * `created`.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet bring the
 * newly created row, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**.
 *
 * Why the absence of an error proves absolutely nothing here: the server
 * write-path returns **2xx always on purpose** (a 4xx would block the client
 * queue forever, see `infra/PENDING_WORK.md` §1.5.7). That case is a
 * DIFFERENT path — there the server rejects and silences —, but the criterion is
 * the same: a user write that vanishes without trace is the worst failure
 * mode. In `gym-log-app` the motivating case is literal: sets
 * (`workout_set`) of a newly created session that the user edits at the
 * gym, with intermittent connection — the weight or reps just logged
 * can evaporate without any error.
 *
 * Contract of this module: write, **REREAD**, compare. If the value did not
 * land it retries with backoff; if upon exhaustion it still has not landed, it
 * **THROWS** — caller communicates it instead of leaving user staring at a
 * field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberate) ───────────────────────────
 * Hard `DELETE`s (`deleteExerciseMutation`, `deleteRoutineMutation`,
 * `deleteSessionMutation`, `deleteSetMutation`, `deleteBodyMeasurementMutation`)
 * are not verified: their natural check would be "row is no longer present", and
 * "absent" is precisely the ambiguous state of this window — cannot distinguish
 * "was deleted" from "had not arrived yet". Verifying them would require another signal
 * (CRUD queue itself), not a reread. Signal that would reopen this: a permanent
 * deletion that is genuinely lost.
 *
 * ── `scope`: tenant isolation, not just correctness ─────────────────────────
 * Brought from `comandero-app` (`f8f1049`), where removing the
 * `organization_id` condition from `WHERE` when migrating to `patchRowVerified` **opened
 * cross-tenant writes between organizations**: `infra/sync_streams.yaml`
 * syncs ALL organizations where the user is a `member` (not
 * just active — YAML itself says "client scopes"), and row `id`
 * travels in URL. Without extra filter, an `UPDATE … WHERE id = ?` launched
 * from organization A context landed equally on a row of organization
 * B if `id` matched — tenant `WHERE` was not
 * redundant with PK, it was isolation in depth. `scope` restores that
 * filter **applied to both UPDATE and reread**: if applied only
 * to one, verification would lie (rereads without filter and confirms
 * a write that actually landed outside scope, or vice versa). A
 * row not matching scope is treated same as absent row: write is
 * never confirmed and function terminates throwing
 * `LocalWriteLostError` — same mechanism as checkpoint window, but
 * for the right reason (not yours, not that it arrived late). In
 * `gym-log-app` the only `WHERE` with existing extra condition was
 * `exercise` (`and(eq(id), eq(user_id))`, only editable own exercises —
 * global catalog is read-only): preserved via `options.scope`.
 *
 * ── THREE pieces on `updated_at`, and they are THREE different things ──────
 * They overlap in column name but not in behavior — each
 * has its own flag, and none implies the others:
 *
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` of each attempt.
 *   2. **Ignore `updated_at` brought by `patch`** (always, no flag):
 *      never compared nor passed through to SET as is.
 *   3. **Verify BY `updated_at`** (`verifyBy: "updated_at"`, opt-in): use
 *      stamped seal as landing proof instead of comparing patch by value.
 *
 * ── 1. `hasUpdatedAt: false` — tables lacking that column ────────────────────
 * Brought from `vigia-fleet-app` (`6766c06`): some tables lack `updated_at` column,
 * and canon stamped it unconditionally — would have blown up at runtime
 * against real SQLite (nonexistent column), a failure mock test doubles
 * DO NOT catch because they lack the real schema behind them. With
 * `hasUpdatedAt: false` the `SET` does not touch that column at all, and
 * `verifyBy: "updated_at"` becomes invalid (throws on entry, before writing
 * anything, instead of failing against SQLite halfway through). In `gym-log-app`
 * ALL converted tables (`exercise`, `routine`, `routine_exercise`,
 * `workout_session`, `workout_set`, `body_measurement`, `user`) have
 * `updated_at` (verified in `packages/powersync/src/schema.ts`, not assumed),
 * so no mutation in this repo needs `hasUpdatedAt: false` today —
 * flag lives here for the next table that lacks it.
 *
 * ── 2. Ignore `updated_at` incoming in `patch` itself ────────────────────────
 * Brought from `agape-church-app`: multiple mutations reuse a single `values`
 * object between their INSERT and UPDATE branches, and that object already carries
 * its own `updated_at` for INSERT. If leaked without stripping, it would be
 * compared as domain field against FRESH `updated_at` stamped by this
 * helper on each attempt — which never matches — and patch would fail
 * ALWAYS for the wrong reason (not because write did not land, but
 * because imported `updated_at` was already stale before
 * writing). Ignored unconditionally, not just when it bites: not a
 * domain field, controlled by this module. Independent of `hasUpdatedAt`:
 * ignored equally even if table lacks column (a patch bringing it
 * there would be caller error, not something to slip into SET).
 *
 * ── 3. `verifyBy: "updated_at"` — EXPLICIT mode, never automatic ────────────
 * Also from `agape-church-app`: atomic increment
 * `sql\`MAX(0, COALESCE(x, 0) + ?)\`` in patch — read-add-write in JS
 * would reopen double-tap race that SQL prevents on purpose. A
 * SQL expression lacks a predictable expected value: cannot
 * compare by value after reread. First draft of `agape` resolved
 * this with automatic *fallback* (if no literal fields remain in
 * patch, compare `updated_at` instead); discarded on purpose: a
 * silent fallback converts "cannot compare" into "assume valid",
 * masking cases where patch should NOT be comparable by
 * value (caller error, mistyped field). With
 * `verifyBy: "updated_at"` caller explicitly declares "verify that
 * THIS write landed, not that value matches expected" — if omitted and
 * patch brings SQL expression, value comparison can never match and
 * function terminates throwing `LocalWriteLostError` even if write
 * landed: **closed failure**, not false green. `gym-log-app` currently has
 * no atomic increment in mutations (checked, not assumed);
 * mode lives here for next one that appears.
 *
 * ── Why NO `patchRowsVerified` / `waitForRowsPresent` (batched) ────────────
 * `folio-office-app` (`1a7b5b3`) and `apunte-digital-app` (`4cc1a014`) added
 * them to move a page and its cohort of subpages at once
 * (`WHERE id IN (...)`, lands only when ALL batch rows reflect
 * patch). `gym-log-app` has no mutation touching more than one row
 * per write with `inArray` (verified in `packages/shared/src/**\/mutations.ts`
 * before deciding this, not assumed) — so bringing batch primitive
 * here would be dead code: the kind of debt that bit in
 * `lib/gestures`, diverging in 4 ways when copied to repos that did not
 * need it. Signal that would reopen this: a mutation starting to move more
 * than one row with same timestamp.
 *
 * ── Second class: upsert deciding INSERT-vs-UPDATE with own SELECT ───────────
 *
 * `patchRowVerified`/`waitForRowPresent` protect write against a row
 * whose `id` is ALREADY known. A different class neither covers:
 * an upsert deciding whether to INSERT or UPDATE with own SELECT, because there is
 * no `id` to reread until that branch is decided. That SELECT falls into SAME
 * checkpoint window: if row exists but just reappeared after a
 * rebuild, SELECT sees it absent and code INSERTs a new row — data is not
 * lost, row is DUPLICATED, without any visible error.
 *
 * Characterized across five clones (`folio-office-app`, `habit-streak-app`,
 * `boveda-vault-app`, `vigia-fleet-app`, `meal-planner-app`). Resolved in
 * `stockpilot-app` (`b8c4bc8a`) with `readMatchingRowVerified`.
 *
 * **`gym-log-app` currently does not have this class** (checked, not assumed: the 10
 * `create*Mutation`/`log*Mutation`/`add*Mutation` in
 * `packages/shared/src/gym/mutations.ts` generate `id` with `generateId()`
 * and INSERT directly; not a single `SELECT` followed by insert/update branch
 * against PowerSync view). `readMatchingRowVerified` lives here HOSTED,
 * same as in canon, for the day a mutation in this repo
 * needs to decide INSERT-vs-UPDATE by content rather than known `id`
 * — do not delete as "unused code".
 *
 * Two ways to close this class, not interchangeable — full guide
 * (with criteria for choosing) in
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md`:
 *
 *   1. **Explicit `isNew` from caller.** If caller already
 *      knows whether creating or updating (comes from distinct "New" button
 *      vs "Save", or distinct `/new` route vs
 *      `/:id/edit`), pass as parameter and skip decision SELECT
 *      entirely. Simpler and zero cost.
 *   2. **`readMatchingRowVerified`.** When row is identified by
 *      CONTENT (composite key, not `id` brought by caller) and thus
 *      no one knows in advance if it exists. Retries decision read
 *      with same backoff as rest of module, BEFORE caller
 *      concludes "does not exist yet, must create".
 */

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * checkpoint (measured at ~1 s against the local stack) without leaving a fast
 * interaction — logging a set, ending a session — hung forever if
 * row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — tenant isolation in depth. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions row MUST ALSO satisfy, in addition to `id` —
   * typically `user_id`/`organization_id`. Applied to both UPDATE and
   * reread: a row failing scope is treated same as absent
   * row (see security note above).
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether table has `updated_at` column. Default `true` (standard
   * behavior: stamped on each attempt). Set to `false`
   * on tables that LACK that column (see note above, piece 1) — with
   * `false`, `patchRowVerified` never inserts it into `SET`, and `verifyBy:
   * "updated_at"` ceases to be valid (no column to compare).
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify write landed, when patch cannot be compared
   * by value (e.g. atomic increment SQL expression).
   * EXPLICIT mode, never automatic — see note above, piece 3. `"updated_at"`
   * compares seal stamped by this helper on each attempt: proves THIS
   * write landed, not that final value matches expected. Requires
   * `hasUpdatedAt` not `false`.
   */
  verifyBy?: "updated_at";
}

/** Error for a local-first write that failed to land in view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` without scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads row by id (+ `scope` if given), or `undefined` if not in
 * local view or fails scope — same result as "absent row", on
 * purpose (see security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer shape of generic `SQLiteTable`: select
  // resolves to `{ [x: string]: any }[]`, so annotating destination suffices — no
  // need to pass through `unknown`, which would narrow without real check.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * If `options.hasUpdatedAt` is not `false` (standard case), `updated_at` is
 * set by this function on each attempt — any `updated_at` in
 * `patch` itself is ALWAYS ignored, both writing and comparing (see
 * note above, piece 2); remainder of `patch` must carry only domain
 * fields. With `hasUpdatedAt: false` (tables lacking column, see piece 1)
 * `SET` does not touch it at all. With `options.verifyBy === "updated_at"` patch
 * is not compared by value: verifies seal of THIS attempt
 * landed (see note above, piece 3) — combining with `hasUpdatedAt: false`
 * is configuration error and throws immediately, before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — table lacks that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for row to EXIST in local view before mutating (+ `scope`
 * if given, with same isolation criteria as `patchRowVerified`).
 *
 * For mutations that are not a simple patch — a transaction
 * restoring a routine version, or swapping `position` between two
 * routine exercises. Cannot retry within transaction (would
 * sleep holding write lock), and damage of operating on an
 * absent row can be worse than losing a field.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Rereads, with same backoff as rest of module, a row
 * identified by CONTENT rather than `id` — case of `stock_quant` in
 * `stockpilot-app` (product×location×lot key), or comment thread /
 * daily log / device enrollment deciding INSERT-vs-UPDATE
 * inspecting its own content columns. See "Second class" note
 * above: `gym-log-app` currently HAS NO caller for this — lives here
 * HOSTED for when needed.
 *
 * ALWAYS called before opening transaction deciding INSERT-vs-UPDATE, and
 * NEVER inside it — same reason as `waitForRowPresent`: retrying with
 * write lock already held sleeps entire transaction, not just
 * read. When multiple rows must be resolved (multiple order lines,
 * for example), resolve in PARALLEL (`Promise.all`) before
 * transaction: genuinely new line does not pay latency of
 * backoff from another line that needed retries.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting backoff
 * is NOT an error here: genuine absence (first time touching that
 * content combination) is VALID result, so this function
 * returns `undefined` instead of throwing, and caller proceeds to INSERT
 * exactly as if it had not retried. Backoff only narrows
 * false-negative window; cannot eliminate completely without another signal (the
 * CRUD queue itself).
 *
 * `read` must repeat SAME query on each attempt — a function that
 * reconstructs `where` over `db`/`trx`, not already resolved promise
 * reused without touching DB again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
